import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { Card, Spin, Alert, Select, Checkbox, Typography, Space, Button, Tabs, Statistic, Row, Col } from "antd";
import { MapContainer, TileLayer, GeoJSON, LayersControl } from "react-leaflet";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { adjacentCropService } from "../../services/api";
import type { AdjacentCrop, CropSpecies } from "../../types/adjacent-crop";
import { ArrowLeftOutlined, EnvironmentOutlined, CheckCircleOutlined, CloseCircleOutlined, AppstoreOutlined } from "@ant-design/icons";
import { usePageTitle } from "../../hooks";

const { Title } = Typography;
const { Option } = Select;

// Fix for default marker icons in Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

interface AdjacentCropMapProps {
  farmId?: number;
}

/**
 * AdjacentCropMap Component
 * 
 * Displays adjacent crops on a map with filtering capabilities.
 * Shows crops as markers or polygons based on their geometry type.
 * Uses different colors for open vs closed crops.
 * 
 * Features:
 * - Filter by species
 * - Filter by status (open/closed)
 * - Click on crop to see details
 * - Satellite and street map layers
 */
export const AdjacentCropMap: React.FC<AdjacentCropMapProps> = ({ farmId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams<{ farmId?: string }>();
  
  // Set page title
  usePageTitle(t("adjacent_crops.map.title", "Mapa de Cultivos Adyacentes"));
  
  // Use farmId from props or URL params
  const effectiveFarmId = farmId || (params.farmId ? parseInt(params.farmId) : undefined);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adjacentCrops, setAdjacentCrops] = useState<AdjacentCrop[]>([]);
  const [species, setSpecies] = useState<CropSpecies[]>([]);
  const [controlPointStats, setControlPointStats] = useState<any>(null);
  
  // Filters
  const [selectedSpecies, setSelectedSpecies] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string[]>(['open', 'closed']);
  
  const defaultCenter: [number, number] = [39.4699, -0.3763]; // Valencia, Spain
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [mapZoom, setMapZoom] = useState(13);

  // Fetch species for filter
  useEffect(() => {
    const fetchSpecies = async () => {
      try {
        const speciesData = await adjacentCropService.getCropSpecies();
        setSpecies(speciesData);
      } catch (err) {
        console.error("Error fetching species:", err);
      }
    };
    
    fetchSpecies();
  }, []);

  // Fetch adjacent crops
  useEffect(() => {
    const fetchAdjacentCrops = async () => {
      if (!effectiveFarmId) {
        setError(t("adjacent_crops.map.no_farm_id", "No farm ID provided"));
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch crops with filters
        const filters: any = {
          farm: effectiveFarmId,
        };
        
        if (selectedSpecies) {
          filters.species = selectedSpecies;
        }
        
        if (selectedStatus.length > 0 && selectedStatus.length < 2) {
          filters.status = selectedStatus[0];
        }
        
        const crops = await adjacentCropService.getAdjacentCrops(filters);
        setAdjacentCrops(crops);
        
        // Calculate map center from crops with geometry
        if (crops.length > 0) {
          const cropsWithGeom = crops.filter(c => c.geom);
          if (cropsWithGeom.length > 0) {
            // Calculate center from all geometries
            let totalLat = 0;
            let totalLng = 0;
            let count = 0;
            
            cropsWithGeom.forEach(crop => {
              if (crop.geom) {
                if (crop.geom.type === 'Point') {
                  totalLng += crop.geom.coordinates[0];
                  totalLat += crop.geom.coordinates[1];
                  count++;
                } else if (crop.geom.type === 'Polygon') {
                  // Use first coordinate of polygon
                  const coords = crop.geom.coordinates[0][0];
                  totalLng += coords[0];
                  totalLat += coords[1];
                  count++;
                }
              }
            });
            
            if (count > 0) {
              setMapCenter([totalLat / count, totalLng / count]);
              setMapZoom(14);
            }
          }
        }
        
        setLoading(false);
      } catch (err: any) {
        console.error("Error fetching adjacent crops:", err);
        setError(err.message || t("adjacent_crops.map.fetch_error", "Error loading adjacent crops"));
        setLoading(false);
      }
    };
    
    fetchAdjacentCrops();
  }, [effectiveFarmId, selectedSpecies, selectedStatus, t]);

  // Style for GeoJSON features based on status
  const getFeatureStyle = (feature: any) => {
    const status = feature.properties?.status;
    
    if (status === 'closed') {
      return {
        fillColor: "#8c8c8c",
        weight: 2,
        opacity: 1,
        color: "#595959",
        fillOpacity: 0.4,
      };
    }
    
    // Open crops - green
    return {
      fillColor: "#52c41a",
      weight: 2,
      opacity: 1,
      color: "#389e0d",
      fillOpacity: 0.6,
    };
  };

  // Popup content for each feature
  const onEachFeature = (feature: any, layer: any) => {
    if (!feature.properties) return;
    
    const props = feature.properties;
    const popupContent = `
      <div style="min-width: 200px;">
        <h4 style="margin: 0 0 8px 0;">${props.name || t("adjacent_crops.unnamed", "Unnamed")}</h4>
        <div style="margin-bottom: 4px;"><strong>${t("adjacent_crops.species", "Species")}:</strong> ${props.species_name || "-"}</div>
        <div style="margin-bottom: 4px;"><strong>${t("adjacent_crops.variety", "Variety")}:</strong> ${props.variety_name || t("adjacent_crops.unknown", "Unknown")}</div>
        <div style="margin-bottom: 4px;"><strong>${t("adjacent_crops.surface", "Surface")}:</strong> ${props.total_estimated_surface || 0} ha</div>
        <div style="margin-bottom: 4px;"><strong>${t("adjacent_crops.status", "Status")}:</strong> ${props.status === 'open' ? t("adjacent_crops.open", "Open") : t("adjacent_crops.closed", "Closed")}</div>
        <div style="margin-top: 8px;">
          <a href="/adjacent-crops/${props.id}" style="color: #1890ff;">${t("adjacent_crops.view_details", "View Details")}</a>
        </div>
      </div>
    `;
    
    layer.bindPopup(popupContent);
  };

  // Convert adjacent crops to GeoJSON FeatureCollection
  const cropsToGeoJSON = (): GeoJSON.FeatureCollection => {
    const features = adjacentCrops
      .filter(crop => crop.geom) // Only crops with geometry
      .map(crop => ({
        type: "Feature" as const,
        id: crop.id,
        geometry: crop.geom!,
        properties: {
          id: crop.id,
          name: crop.name,
          species_name: crop.species_name,
          variety_name: crop.variety_name,
          status: crop.status,
          total_estimated_surface: crop.total_estimated_surface,
          producer_name: crop.producer_name,
          farm_name: crop.farm_name,
        },
      }));
    
    return {
      type: "FeatureCollection",
      features,
    };
  };

  // Render point geometries as circle markers
  const pointToLayer = (feature: any, latlng: L.LatLng) => {
    const status = feature.properties?.status;
    const color = status === 'closed' ? '#8c8c8c' : '#52c41a';
    const fillColor = status === 'closed' ? '#595959' : '#389e0d';
    
    return L.circleMarker(latlng, {
      radius: 8,
      fillColor: fillColor,
      color: color,
      weight: 2,
      opacity: 1,
      fillOpacity: 0.6,
    });
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "500px" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message={t("adjacent_crops.map.error", "Error")}
        description={error}
        type="error"
        showIcon
      />
    );
  }

  const geoJsonData = cropsToGeoJSON();
  const cropsWithoutGeometry = adjacentCrops.filter(crop => !crop.geom).length;

  // Calculate adjacent crops statistics
  const adjacentCropsStats = {
    total: adjacentCrops.length,
    open: adjacentCrops.filter(c => c.status === 'open').length,
    closed: adjacentCrops.filter(c => c.status === 'closed').length,
    withGeometry: adjacentCrops.filter(c => c.geom).length,
    totalSurface: adjacentCrops.reduce((sum, c) => sum + (c.total_estimated_surface || 0), 0),
  };

  return (
    <div>
      {/* Header with filters */}
      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>
              {t("adjacent_crops.map.title", "Adjacent Crops Map")}
            </Title>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(-1)}
            >
              {t("common.back", "Back")}
            </Button>
          </div>
          
          <Space wrap>
            <Select
              style={{ width: 200 }}
              placeholder={t("adjacent_crops.filter.species", "Filter by species")}
              allowClear
              value={selectedSpecies}
              onChange={setSelectedSpecies}
            >
              {species.map(s => (
                <Option key={s.id} value={s.id}>
                  {s.name}
                </Option>
              ))}
            </Select>
            
            <Checkbox.Group
              options={[
                { label: t("adjacent_crops.open", "Open"), value: 'open' },
                { label: t("adjacent_crops.closed", "Closed"), value: 'closed' },
              ]}
              value={selectedStatus}
              onChange={(values) => setSelectedStatus(values as string[])}
            />
          </Space>
          
          <div style={{ fontSize: 14, color: '#595959' }}>
            {t("adjacent_crops.map.showing", "Showing")} {geoJsonData.features.length} {t("adjacent_crops.map.crops_on_map", "crops on map")}
            {cropsWithoutGeometry > 0 && (
              <span style={{ marginLeft: 8 }}>
                ({cropsWithoutGeometry} {t("adjacent_crops.map.without_geometry", "without location")})
              </span>
            )}
          </div>
        </Space>
      </Card>

      {/* Statistics Tabs */}
      <Card style={{ marginBottom: 16 }}>
        <Tabs
          defaultActiveKey="adjacent-crops"
          items={[
            {
              key: 'adjacent-crops',
              label: (
                <span>
                  <AppstoreOutlined />
                  {t("adjacent_crops.statistics.title", "Cultivos Colindantes")}
                </span>
              ),
              children: (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("adjacent_crops.statistics.total", "Total Cultivos")}
                        value={adjacentCropsStats.total}
                        prefix={<AppstoreOutlined />}
                        valueStyle={{ color: "#1890ff" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("adjacent_crops.statistics.open", "Abiertos")}
                        value={adjacentCropsStats.open}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("adjacent_crops.statistics.closed", "Cerrados")}
                        value={adjacentCropsStats.closed}
                        prefix={<CloseCircleOutlined />}
                        valueStyle={{ color: "#8c8c8c" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("adjacent_crops.statistics.surface", "Superficie Total")}
                        value={adjacentCropsStats.totalSurface}
                        suffix="ha"
                        precision={2}
                        prefix={<EnvironmentOutlined />}
                        valueStyle={{ color: "#722ed1" }}
                      />
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'control-points',
              label: (
                <span>
                  <EnvironmentOutlined />
                  {t("controlPoint.statistics.title", "Puntos de Control")}
                </span>
              ),
              children: controlPointStats ? (
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("controlPoint.statistics.total", "Total Puntos")}
                        value={controlPointStats.total || 0}
                        prefix={<EnvironmentOutlined />}
                        valueStyle={{ color: "#1890ff" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("controlPoint.statistics.open", "Abiertos")}
                        value={controlPointStats.open || 0}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("controlPoint.statistics.closed", "Cerrados")}
                        value={controlPointStats.closed || 0}
                        prefix={<CloseCircleOutlined />}
                        valueStyle={{ color: "#8c8c8c" }}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <Card>
                      <Statistic
                        title={t("controlPoint.statistics.compliant", "Conformes")}
                        value={controlPointStats.compliant || 0}
                        prefix={<CheckCircleOutlined />}
                        valueStyle={{ color: "#52c41a" }}
                      />
                    </Card>
                  </Col>
                </Row>
              ) : (
                <Alert
                  message={t("controlPoint.statistics.not_available", "Estadísticas no disponibles")}
                  description={t("controlPoint.statistics.not_available_desc", "Las estadísticas de puntos de control se cargarán cuando estén disponibles.")}
                  type="info"
                  showIcon
                />
              ),
            },
          ]}
        />
      </Card>

      {/* Map */}
      <Card>
        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: "600px", width: "100%" }}
          scrollWheelZoom={true}
        >
          <LayersControl position="topright">
            {/* Satellite Layer */}
            <LayersControl.BaseLayer checked name={t("map.satellite", "Satellite")}>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
              />
            </LayersControl.BaseLayer>

            {/* Street Map Layer */}
            <LayersControl.BaseLayer name={t("map.street", "Street Map")}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* Adjacent Crops Layer */}
          {geoJsonData.features.length > 0 && (
            <GeoJSON
              data={geoJsonData}
              style={getFeatureStyle}
              onEachFeature={onEachFeature}
              pointToLayer={pointToLayer}
            />
          )}
        </MapContainer>
      </Card>
    </div>
  );
};

export default AdjacentCropMap;
